import { getClientEnv, isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import {
  createPqrPagination,
  mapPqrAdminListItem,
  mapPqrRequestDetails,
  mapPqrThreadMessage,
} from "@/features/pqr/pqrMappers";
import {
  pqrPageSize,
  type PqrAdminPage,
  type PqrReplyRecord,
  type PqrRequestDetails,
  type PqrStatus,
  type PqrThreadMessage,
} from "@/features/pqr/pqr.types";
import { PqrClientError } from "@/features/pqr/api/pqrClient";

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new PqrClientError("generic", "Supabase environment variables are not configured.");
  }
}

export async function loadAdminPqrPage(page = 1): Promise<PqrAdminPage> {
  ensureConfigured();

  const requestedPage = Math.max(page, 1);
  const rangeFrom = (requestedPage - 1) * pqrPageSize;
  const rangeTo = rangeFrom + pqrPageSize - 1;

  const result = await supabase
    .from("pqr_submissions")
    .select("id, ticket_ref, name, email, category, subject, status, created_at, user_id", {
      count: "exact",
    })
    .order("created_at", { ascending: false })
    .range(rangeFrom, rangeTo);

  if (result.error) {
    throw new PqrClientError("generic", result.error.message, result.error);
  }

  const pagination = createPqrPagination({
    page,
    totalCount: result.count ?? 0,
  });

  return {
    items: (result.data ?? []).map((row) => mapPqrAdminListItem(row)),
    page: pagination.page,
    pageCount: pagination.pageCount,
    totalCount: pagination.totalCount,
  };
}

export async function loadAdminPqrRequestDetails(requestId: string): Promise<PqrRequestDetails> {
  ensureConfigured();

  const result = await supabase
    .from("pqr_submissions")
    .select("*")
    .eq("id", requestId)
    .single();

  if (result.error || !result.data) {
    throw new PqrClientError("generic", result.error?.message, result.error);
  }

  return mapPqrRequestDetails(result.data);
}

export async function updatePqrStatus(requestId: string, status: PqrStatus) {
  ensureConfigured();

  const result = await supabase.rpc("admin_update_pqr_status", {
    next_status: status,
    target_submission_id: requestId,
  });

  if (result.error || !result.data || typeof result.data !== "object") {
    throw new PqrClientError("generic", result.error?.message, result.error);
  }

  return result.data as { id?: string; status?: string };
}

export async function replyToPqrSubmission(
  requestId: string,
  message: string,
): Promise<PqrThreadMessage> {
  ensureConfigured();

  const {
    data: { session },
  } = await supabase.auth.getSession();

  const headers = new Headers({
    "Content-Type": "application/json; charset=utf-8",
  });

  if (!session?.access_token) {
    throw new PqrClientError("PQR_UNAUTHORIZED");
  }

  headers.set("Authorization", `Bearer ${session.access_token}`);

  const response = await fetch(`${getClientEnv().supabaseUrl}/functions/v1/pqr-admin`, {
    body: JSON.stringify({
      action: "reply_to_submission",
      message,
      submissionId: requestId,
    }),
    headers,
    method: "POST",
  });

  const body = await response.json().catch(() => ({})) as {
    code?: string;
    message?: string;
    reply?: Record<string, unknown>;
  };

  if (!response.ok || !body.reply) {
    throw new PqrClientError(
      body.code === "PQR_UNAUTHORIZED"
        ? "PQR_UNAUTHORIZED"
        : body.code === "PQR_INVALID_REPLY_MESSAGE"
          ? "PQR_INVALID_REPLY_MESSAGE"
          : body.code === "PQR_REPLY_UNAVAILABLE"
            ? "PQR_REPLY_UNAVAILABLE"
            : "generic",
      typeof body.message === "string" ? body.message : "Could not reply to the PQR submission.",
      body,
    );
  }

  return mapPqrThreadMessage(body.reply as PqrReplyRecord);
}

export async function deletePqrSubmission(requestId: string) {
  ensureConfigured();

  const {
    data: { session },
  } = await supabase.auth.getSession();

  const headers = new Headers({
    "Content-Type": "application/json; charset=utf-8",
  });

  if (!session?.access_token) {
    throw new PqrClientError("PQR_UNAUTHORIZED");
  }

  headers.set("Authorization", `Bearer ${session.access_token}`);

  const response = await fetch(`${getClientEnv().supabaseUrl}/functions/v1/pqr-admin`, {
    body: JSON.stringify({
      action: "delete_submission",
      submissionId: requestId,
    }),
    headers,
    method: "POST",
  });

  const body = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) {
    throw new PqrClientError(
      body.code === "PQR_UNAUTHORIZED" ? "PQR_UNAUTHORIZED" : "generic",
      typeof body.message === "string" ? body.message : "Could not delete the PQR submission.",
      body,
    );
  }

  return body;
}
